/**
 * AI Router Settings Types
 * These types define the configuration for the AI Router feature
 * stored in the Firestore document at `chatflows/<store_id>`
 */

/**
 * Current operational status of AI Router
 */
export enum AIRouterStatus {
  /** AI Router is fully enabled for all users */
  ENABLED = "ENABLED",
  /** AI Router is disabled */
  DISABLED = "DISABLED",
  /** AI Router is enabled only for testing (works only on test screen) */
  TEST_MODE = "TEST_MODE",
}

/**
 * When to check for keyword matches
 */
export enum KeywordMatchFrequency {
  /** Check only on the first message */
  FIRST_MESSAGE = "FIRST_MESSAGE",
  /** Check after every message */
  AFTER_EVERY_MESSAGE = "AFTER_EVERY_MESSAGE",
}

/**
 * AI Router Settings interface
 * Stored in the `aiRouterSettings` field of the `chatflows/<store_id>` document
 */
export interface AIRouterSettings {
  /** Stored status; missing means the store never saved one (on for WhatsApp and Instagram only) */
  status?: AIRouterStatus;
  /** When to check for keyword matches */
  keywordMatchFrequency: KeywordMatchFrequency;
  /** Last updated timestamp (milliseconds since epoch) */
  updatedAt?: number;
}

/**
 * Default AI Router Settings when not configured.
 * Status is left unset so Email can still tell "never saved" apart from an explicit ENABLED.
 */
export const DEFAULT_AI_ROUTER_SETTINGS: AIRouterSettings = {
  keywordMatchFrequency: KeywordMatchFrequency.FIRST_MESSAGE,
};

/**
 * Whether the AI Router is on for WhatsApp and Instagram: on unless the store saved DISABLED
 */
export const isAiRouterEnabled = (settings: AIRouterSettings | null | undefined): boolean => {
  return settings?.status !== AIRouterStatus.DISABLED;
};

/**
 * Whether the store explicitly turned the AI Router on. Email still needs this.
 */
export const isAiRouterExplicitlyEnabled = (
  settings: AIRouterSettings | null | undefined
): boolean => {
  return settings?.status === AIRouterStatus.ENABLED || settings?.status === AIRouterStatus.TEST_MODE;
};

/**
 * Helper function to check if AI Router is in test mode
 * @param settings - The AI Router settings
 * @returns true if AI Router is in test mode
 */
export const isAiRouterInTestMode = (settings: AIRouterSettings | null | undefined): boolean => {
  if (!settings) return false;
  return settings.status === AIRouterStatus.TEST_MODE;
};
